import { reaction } from 'mobx';
import {
  types, flow, addDisposer, Instance,
} from 'mobx-state-tree';
import Cookies from 'universal-cookie';
import * as Sentry from '@sentry/browser';
import getAppEnv from './utils/getAppEnv';
import getAppRoot from './utils/getAppRoot';
import UserQuery from './queries/user/UserQuery';
import AuthQuery from './queries/user/AuthQuery';
import { syncParamsWithStore } from './Router/utils';
import { DEFAULT_BOTTOM_PANEL_HEIGHT } from '../../constants';
import ResetPasswordQuery from './queries/user/ResetPasswordQuery';
import ValidateResetPasswordTokenQuery from './queries/user/ValidateResetPasswordTokenQuery';
import { ApplicationType } from './Router/constants';
import { SsoClientApps, SsoProvider, SupportSessionDtoModelType } from './models';
import { NotificationType, notification } from '@routific/hive';
import Gleap from 'gleap';
import AnalyticsEvents from '../../analytics/analyticsEvents';
import { AnalyticsEventsProps } from '../../analytics/analyticsEventsProps';
import posthog from 'posthog-js/dist/module.full';

const cookies = new Cookies();
const HIVE_TABLE_VIEW_STORAGE_PREFIX = 'hive.table.view.';

// The timeline is the only panel a user can still resize, so it's the only one
// whose size is worth remembering across sessions.
const setStore = (ui: any, bottom: string | null) => {
  ui.setCurrentBottomPanelHeight(bottom ? parseInt(bottom, 10) : DEFAULT_BOTTOM_PANEL_HEIGHT);
};

const clearLocalStorageForLogout = () => {
  const tableViewEntries = Object.keys(window.localStorage)
    .filter((key) => key.startsWith(HIVE_TABLE_VIEW_STORAGE_PREFIX))
    .map((key) => [key, window.localStorage.getItem(key)] as const);

  window.localStorage.clear();

  tableViewEntries.forEach(([key, value]) => {
    if (value !== null) {
      window.localStorage.setItem(key, value);
    }
  });
};

const Auth = types.model('Auth')
  .props({
    isVerified: types.optional(types.boolean, false),
    isVerifying: types.optional(types.boolean, false),
    token: types.maybe(types.string),
    userId: types.maybe(types.string),
  })
  .views((self) => ({
    get isAuthenticated(): boolean {
      return self.isVerified && self.token != null;
    },
    get isDebugSession(): boolean {
      if (!self.userId) return false;
      return !!getAppRoot(self).model.userDtos.get(self.userId)?.supportSessionUuid;
    },
    get supportSession(): SupportSessionDtoModelType | undefined {
      if (self.isVerified && self.token != null && self.userId && !!getAppRoot(self).model.userDtos.get(self.userId)?.supportSessionUuid) {
        return getAppRoot(self).model.userDtos.get(self.userId)?.activeSupportSession;
      }

      return undefined;
    },
  }))
  .actions((self) => ({
    authenticate(token: string) {
      if (!self.isVerified) {
        getAppEnv(self).setToken(token);
        self.token = token;
        self.isVerified = true;
        addDisposer(self, reaction(() => self.token, () => {
          if (token) {
            const bottom = localStorage.getItem('currentBottomPanelHeight');
            setStore(getAppRoot(self).ui, bottom);
          }
        }, { fireImmediately: true }));
      }
    },
    unauthenticate() {
      getAppEnv(self).clearToken();
      clearLocalStorageForLogout();
      self.token = undefined;
      self.isVerified = false;
      self.userId = undefined;
      syncParamsWithStore({}, getAppRoot(self).router); // Resets the found URL params upon logout
    },
    setUserFromToken: flow(function* () {
      try {
        const user = yield getAppRoot(self).model.queryMe({}, UserQuery, { fetchPolicy: 'no-cache' }).currentPromise();

        getAppRoot(self).account.setUser(user.me.id);
        self.userId = user.me.id;

        getAppRoot(self).account.fetchPendingInvites(); // Fire and forget - don't yield to avoid race condition

        getAppRoot(self).organization.setDistanceUnit(user.me.organization.settings.distanceUnit);

        getAppRoot(self).account.gleapIdentify(user.me.uuid, {
          name: user.me.fullname,
          email: user.me.email,
          orgId: user.me.organization.id,
          orgName: user.me.organization.name,
          legacyUuid: user.me.metadata?.uuid,
        });

        Sentry.setUser({ id: user.me.uuid });
      } catch {
        getAppRoot(self).account.clearUser();
        self.userId = undefined;
      }
    }),
  }))
  .actions((self) => {
    const performAuthenticationSteps = flow(function* (token: string) {
      self.authenticate(token);
      yield self.setUserFromToken();
    });

    return {
      loginWithToken: flow(function* (token: string) {
        self.isVerified = false;
        self.isVerifying = true;
        try {

          if (token != null) {
            yield performAuthenticationSteps(token);
          } else {
            throw new Error('Invalid Login');
          }
        } catch (err) {
          self.unauthenticate();
          throw (err);
        } finally {
          self.isVerifying = false;
        }
      }),
      loginSSO: flow(function* (authorizationCode: string, identityProvider: SsoProvider) {
        self.isVerified = false;
        self.isVerifying = true;

        try {
          const query = yield getAppRoot(self).model.mutateSsoLogin({
            authorizationCode,
            identityProvider,
            ssoClientApp: SsoClientApps.DISPATCHER_APP,
          }, AuthQuery).currentPromise();

          if (query.ssoLogin.accessToken != null) {
            yield performAuthenticationSteps(query.ssoLogin.accessToken);
            getAppRoot(self).account.identifyAndTrack(
              AnalyticsEvents.SIGN_IN, { loginMethod: AnalyticsEventsProps.User.AuthenticationMethod[identityProvider] ?? identityProvider },
            );
          } else {
            throw new Error('SSO login failed - missing access token');
          }

        } catch (err) {
          self.isVerifying = false;
          self.isVerified = false;
          self.unauthenticate();
          throw (err);
        }

        self.isVerifying = false;
      }),
      login: flow(function* (email: string, password: string) {
        self.isVerified = false;
        self.isVerifying = true;

        try {
          const query = yield getAppRoot(self).model.mutateLogin({ email, password }, AuthQuery).currentPromise();

          if (query.login.accessToken != null) {
            yield performAuthenticationSteps(query.login.accessToken);
            getAppRoot(self).account.identifyAndTrack(
              AnalyticsEvents.SIGN_IN, { loginMethod: AnalyticsEventsProps.User.AuthenticationMethod.MANUAL },
            );
          } else {
            throw new Error('Login failed - missing access token');
          }
        } catch (err) {
          self.unauthenticate();
          throw (err);
        } finally {
          self.isVerifying = false;
        }

      }),
      verify: flow(function* (token?: string) {
        self.token = self.token || token;

        if (self.token) {
          self.isVerifying = true;

          try {
            self.authenticate(self.token);
            yield self.setUserFromToken();
          } catch (err) {
            self.unauthenticate();
          }

          self.isVerifying = false;
        }
      }),
      createUser: flow(function* (
        fullname: string,
        email: string,
        password: string,
        organizationName: string,
        tempPassword: boolean = false,
        legacyId?: string,
        stripeCustomerId?: string,
      ) {
        const newUser = {
          fullname,
          email,
          password,
          organization: { name: organizationName, stripeCustomerId },
          metadata: legacyId ? { uuid: legacyId } : undefined,
          // TODO: Provide drop-down for this Admin option?
          role: 'DISPATCHER',
          tempPassword,
        };
        yield getAppEnv(self).api.post('/admin/user/create', newUser);
      }),
      changePassword: flow(function* (newPassword: string) {
        const email = getAppRoot(self).account.user?.email;
        yield getAppEnv(self).api.put('/user/password', { email, newPassword });
        // NOTE: User data refresh (setUserFromToken) happens in the UpdatePassword component's
        // goToDashboard function, after the success screen is shown. This prevents component
        // remounting that would reset the local FormState.
      }),
      requestPasswordReset: flow(function* (email: string, applicationType: ApplicationType) {
        yield getAppRoot(self).model.mutateRequestResetPassword({
          email,
          applicationType,
        }).currentPromise();
      }),
      validateResetPasswordToken: flow(function* (token: string) {
        const res = yield getAppRoot(self).model.mutateValidateResetPasswordToken({ token }, ValidateResetPasswordTokenQuery).currentPromise();
        return res.validateResetPasswordToken;
      }),
      resetPassword: flow(function* (token: string, password: string) {
        const res = yield getAppRoot(self).model.mutateResetPassword({ token, newPassword: password }, ResetPasswordQuery).currentPromise();
        return res.resetPassword;
      }),
      logout() {
        Sentry.setUser(null);
        Gleap.clearIdentity();

        if (posthog) {
          posthog.reset();
        }
        getAppRoot(self).account.trackUserForAnalytics(AnalyticsEvents.SIGN_OUT);
        getAppRoot(self).account.clearUser();

        getAppRoot(self).account.resetAnalyticsState();

        self.unauthenticate();
        self.userId = undefined;
        getAppRoot(self).router.reset();
      },
      currentDefaultPath() {
        const originalURL = getAppRoot(self).router.location.state?.redirectTo;
        if (originalURL) {
          const queryParams = getAppRoot(self).router.location.state?.queryParams;
          return { pathname: originalURL, queryParams };
        }
        let path = '/';
        const { user } = getAppRoot(self).account;
        if (user && user.defaultWorkspaceId) {
          path = `/workspace/${user.defaultWorkspaceId}`;
        }
        return { pathname: path };
      },
      closeSupportSession: flow(function* () {
        yield getAppRoot(self).model.mutateCloseSupportSession().currentPromise();
        const applicationToken = cookies.get('application_token');
        cookies.remove('application_token', { path: '/' });

        if (!applicationToken) {
          getAppRoot(self).auth.logout();
        }

        getAppRoot(self).ui.selectWorkspace(null);
        cookies.set('access_token', applicationToken, { secure: true, path: '/' });

        location.reload();
      }),
      startSupportSession: flow(function* (supportOrganizationUuid: string, supportOrganizationName?: string) {
        const id = 'start-support-session';

        try {
          const query = yield getAppRoot(self).model.mutateStartSupportSession({ supportOrganizationUuid }).currentPromise();
          const applicationToken = cookies.get('access_token');
          cookies.set('application_token', applicationToken, { secure: true, path: '/' });

          getAppRoot(self).ui.selectWorkspace(null);
          cookies.set('access_token', query.startSupportSession.accessToken, { secure: true, path: '/' });

          location.reload();
        } catch (err) {
          const errorMessage = supportOrganizationName ? `Couldn't enter debug mode for ${supportOrganizationName}.` : 'Couldn\'t enter debug mode for requested organization.';
          notification({ type: NotificationType.ERROR, id, message: errorMessage });
        }
        return false;
      }),
    };
  });

export default Auth;
export interface IAuthStore extends Instance<typeof Auth> { }
